import React, { createContext, useContext, useState, ReactNode } from 'react';

type Language = 'en' | 'ar';

interface Translations {
  // Header
  appName: string;
  version: string;
  copyright: string;

  // Tabs
  eventTester: string;
  products: string;
  customers: string;

  // Event Tester
  liveEventTester: string;
  liveEventTesterDesc: string;
  pixelId: string;
  customerInfo: string;
  advancedMatching: string;
  add: string;
  newCustomer: string;
  firstName: string;
  lastName: string;
  phoneNumber: string;
  gender: string;
  country: string;
  male: string;
  female: string;
  selectCustomer: string;
  save: string;
  selectProduct: string;
  newProduct: string;
  productName: string;
  valueUsd: string;
  weightKg: string;
  shippingCostPerKg: string;
  purchaseLink: string;
  createProduct: string;
  createCustomer: string;
  eventParameters: string;
  priceSoldLyd: string;
  grossMarginUsd: string;
  contentName: string;
  sendEventNow: string;
  activityLog: string;
  eventsSent: string;
  noEventsFired: string;
  noEventsDesc: string;
  link: string;
  estTotal: string;

  // Products Page
  productsPageTitle: string;
  productsPageDesc: string;
  addProduct: string;
  editProduct: string;
  deleteProduct: string;
  noProducts: string;
  noProductsDesc: string;
  confirmDelete: string;
  productDetails: string;

  // Customers Page
  customersPageTitle: string;
  customersPageDesc: string;
  addCustomer: string;
  editCustomer: string;
  deleteCustomer: string;
  noCustomers: string;
  noCustomersDesc: string;
  confirmDeleteCustomer: string;
  customerDetails: string;

  // Errors
  enterValidPixelId: string;
  selectProductAndPrice: string;
  failedToAddProduct: string;
  failedToDeleteProduct: string;
  failedToUpdateProduct: string;
  failedToAddCustomer: string;
  failedToDeleteCustomer: string;
  failedToUpdateCustomer: string;
}

const translations: Record<Language, Translations> = {
  en: {
    // Header
    appName: 'PIXEL_TESTER',
    version: 'v1.0',
    copyright: 'PIXEL_TESTER // MANHATTAN STYLE',

    // Tabs
    eventTester: 'Event Tester',
    products: 'Products',
    customers: 'Customers',

    // Event Tester
    liveEventTester: 'Sales Event Register',
    liveEventTesterDesc: 'Manually register a sales event to your Meta Pixel.',
    pixelId: 'Pixel ID',
    customerInfo: 'Customer Information',
    advancedMatching: 'Advanced Matching',
    add: 'ADD',
    newCustomer: 'New Customer',
    firstName: 'First Name',
    lastName: 'Last Name',
    phoneNumber: 'Phone Number (e.g. 15551234567)',
    gender: 'Gender',
    country: 'Country',
    male: 'Male',
    female: 'Female',
    selectCustomer: '-- Select a customer --',
    save: 'SAVE',
    selectProduct: 'Select Product',
    newProduct: 'New Product',
    productName: 'Product Name',
    valueUsd: 'Value (USD)',
    weightKg: 'Weight (kg)',
    shippingCostPerKg: 'Shipping Cost per kg (USD)',
    purchaseLink: 'Purchase Link',
    createProduct: 'CREATE PRODUCT',
    createCustomer: 'CREATE CUSTOMER',
    eventParameters: 'Event Parameters',
    priceSoldLyd: 'Price Sold in LYD',
    grossMarginUsd: 'Gross Margin (USD)',
    contentName: 'Content Name (Product/Page)',
    sendEventNow: 'Send Event Now',
    activityLog: 'Activity Log',
    eventsSent: 'events sent',
    noEventsFired: 'No events fired yet.',
    noEventsDesc: 'Enter a Pixel ID and click "Send Event Now" to test.',
    link: 'LINK',
    estTotal: 'Est. Total:',

    // Products Page
    productsPageTitle: 'Products Management',
    productsPageDesc: 'Manage your products for event tracking.',
    addProduct: 'Add Product',
    editProduct: 'Edit Product',
    deleteProduct: 'Delete Product',
    noProducts: 'No products found.',
    noProductsDesc: 'Add your first product to get started.',
    confirmDelete: 'Are you sure you want to delete this product?',
    productDetails: 'Product Details',

    // Customers Page
    customersPageTitle: 'Customers Management',
    customersPageDesc: 'Manage your customers for advanced matching.',
    addCustomer: 'Add Customer',
    editCustomer: 'Edit Customer',
    deleteCustomer: 'Delete Customer',
    noCustomers: 'No customers found.',
    noCustomersDesc: 'Add your first customer to get started.',
    confirmDeleteCustomer: 'Are you sure you want to delete this customer?',
    customerDetails: 'Customer Details',

    // Errors
    enterValidPixelId: 'Please enter a valid Meta Pixel ID before sending events.',
    selectProductAndPrice: 'Please select a product and enter a price sold in LYD.',
    failedToAddProduct: 'Failed to add product',
    failedToDeleteProduct: 'Failed to delete product',
    failedToUpdateProduct: 'Failed to update product',
    failedToAddCustomer: 'Failed to add customer',
    failedToDeleteCustomer: 'Failed to delete customer',
    failedToUpdateCustomer: 'Failed to update customer',
  },
  ar: {
    // Header
    appName: 'PIXEL_TESTER',
    version: 'v1.0',
    copyright: 'PIXEL_TESTER // MANHATTAN STYLE',

    // Tabs
    eventTester: 'اختبار الأحداث',
    products: 'المنتجات',
    customers: 'العملاء',

    // Event Tester
    liveEventTester: 'تسجيل حدث البيع',
    liveEventTesterDesc: 'تسجيل حدث بيع يدوي إلى بكسل ميتا.',
    pixelId: 'معرف البكسل',
    customerInfo: 'معلومات العميل',
    advancedMatching: 'المطابقة المتقدمة',
    add: 'إضافة',
    newCustomer: 'عميل جديد',
    firstName: 'الاسم الأول',
    lastName: 'اسم العائلة',
    phoneNumber: 'رقم الهاتف (مثال: 15551234567)',
    gender: 'الجنس',
    country: 'البلد',
    male: 'ذكر',
    female: 'أنثى',
    selectCustomer: '-- اختر عميل --',
    save: 'حفظ',
    selectProduct: 'اختر منتج',
    newProduct: 'منتج جديد',
    productName: 'اسم المنتج',
    valueUsd: 'القيمة (دولار)',
    weightKg: 'الوزن (كجم)',
    shippingCostPerKg: 'تكلفة الشحن لكل كجم (دولار)',
    purchaseLink: 'رابط الشراء',
    createProduct: 'إنشاء منتج',
    createCustomer: 'إنشاء عميل',
    eventParameters: 'معلمات الحدث',
    priceSoldLyd: 'السعر المباع بالدينار الليبي',
    grossMarginUsd: 'هامش الربح الإجمالي (دولار)',
    contentName: 'اسم المحتوى (منتج/صفحة)',
    sendEventNow: 'إرسال الحدث الآن',
    activityLog: 'سجل النشاط',
    eventsSent: 'أحداث أرسلت',
    noEventsFired: 'لم يتم إرسال أي أحداث بعد.',
    noEventsDesc: 'أدخل معرف البكسل وانقر على "إرسال الحدث الآن" للاختبار.',
    link: 'رابط',
    estTotal: 'الإجمالي التقديري:',

    // Products Page
    productsPageTitle: 'إدارة المنتجات',
    productsPageDesc: 'إدارة منتجاتك لتتبع الأحداث.',
    addProduct: 'إضافة منتج',
    editProduct: 'تعديل المنتج',
    deleteProduct: 'حذف المنتج',
    noProducts: 'لم يتم العثور على منتجات.',
    noProductsDesc: 'أضف منتجك الأول للبدء.',
    confirmDelete: 'هل أنت متأكد أنك تريد حذف هذا المنتج؟',
    productDetails: 'تفاصيل المنتج',

    // Customers Page
    customersPageTitle: 'إدارة العملاء',
    customersPageDesc: 'إدارة عملائك للمطابقة المتقدمة.',
    addCustomer: 'إضافة عميل',
    editCustomer: 'تعديل العميل',
    deleteCustomer: 'حذف العميل',
    noCustomers: 'لم يتم العثور على عملاء.',
    noCustomersDesc: 'أضف عميلك الأول للبدء.',
    confirmDeleteCustomer: 'هل أنت متأكد أنك تريد حذف هذا العميل؟',
    customerDetails: 'تفاصيل العميل',

    // Errors
    enterValidPixelId: 'الرجاء إدخال معرف بكسل ميتا صالح قبل إرسال الأحداث.',
    selectProductAndPrice: 'الرجاء تحديد منتج وإدخال سعر بيع بالدينار الليبي.',
    failedToAddProduct: 'فشل في إضافة المنتج',
    failedToDeleteProduct: 'فشل في حذف المنتج',
    failedToUpdateProduct: 'فشل في تحديث المنتج',
    failedToAddCustomer: 'فشل في إضافة العميل',
    failedToDeleteCustomer: 'فشل في حذف العميل',
    failedToUpdateCustomer: 'فشل في تحديث العميل',
  },
};

interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: Translations;
  dir: 'ltr' | 'rtl';
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

export const LanguageProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [language, setLanguage] = useState<Language>('en');

  const value = {
    language,
    setLanguage,
    t: translations[language],
    dir: language === 'ar' ? 'rtl' : 'ltr',
  };

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (context === undefined) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};
